import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../redux/hooks";
import {
    alternatingWorkordersData,
    getWorkorderLabelRedux,
    getWorkorderManagementBobsRedux,
    onClearWorkorderManagementData,
    workorderManagementBatches,
    workorderManagementBoms,
    workorderManagementData,
    workorderManagementMaterial,
    workorderManagementOperations
} from "../../redux/slices/workorderManagement.feature";
import { useSelector } from "react-redux";
import { IRootState } from "../../types/redux/IRootStore";
import { useTranslation } from "react-i18next";
import AplSettingsSelector from "../../redux/helpers/AplSettingsSelector";
import { AplParameter } from "../../types/aplParameter";
import { parseBool } from "../../aplUtils/extensions";
import { getWorkorderItemsRedux, onSetSelectedWorkorders, onSetShowClosed, onSetWorkorderSearch } from "../../redux/slices/workorderItems.feature";

const WorkorderManagementEdit = (props: { children: JSX.Element }) => {
    const { t } = useTranslation();
    const workorderManagementRedux = useSelector((state: IRootState) => state.workorderManagement);
    const params = useParams();
    const path = window.location.pathname;
    const dispatch = useAppDispatch();
    const aplSettings = AplSettingsSelector();
    const showBobStr = aplSettings.siteParams.find(p => p.aplParameterType === AplParameter.BillOfBatches);
    const showBob = parseBool(showBobStr?.value);
    const showBomStr = aplSettings.siteParams.find(p => p.aplParameterType === AplParameter.ShowBillOfMaterials);
    const showBom = parseBool(showBomStr?.value);
    const navigate = useNavigate();

    const changeTabUrl = (subPage: string) => `/workordermanagement/${params.workorderId}/${subPage}`;
    const changeTab = (subPage: string) => navigate(changeTabUrl(subPage), { replace: true });

    useEffect(() => {
        if (params.workorderId) {
            let workorderId = params.workorderId;
            dispatch(workorderManagementData(workorderId) as any);
            dispatch(getWorkorderLabelRedux(workorderId) as any);
            dispatch(workorderManagementMaterial(workorderId) as any);
            dispatch(workorderManagementOperations(workorderId) as any);
            dispatch(workorderManagementBoms(workorderId) as any);
            dispatch(workorderManagementBatches(workorderId) as any);
            dispatch(getWorkorderManagementBobsRedux(workorderId) as any);
        }
        return () => {
            dispatch(onClearWorkorderManagementData());
        };
    }, [dispatch, params.workorderId]);

    useEffect(() => {
        if (workorderManagementRedux.orderData) {
            dispatch(alternatingWorkordersData(workorderManagementRedux.orderData.lineId) as any);
        }
    }, [workorderManagementRedux.orderData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <React.Fragment>
            <div className="container container mw-100">
                <div className="row mt-2">
                    <div className="col-9">
                        <div className="float-left">
                            {/* <!-- col left here --> */}
                            <nav>
                                <div className="nav nav-tabs ml-0 pl-0" id="nav-tab" role="tablist">
                                    <button
                                        className={`nav-link ${changeTabUrl("") === path ? "active" : ""}`}
                                        id="tab-main"
                                        type="button"
                                        role="tab"
                                        onClick={() => {
                                            changeTab("");
                                        }}
                                    >
                                        {t("main.main")}
                                    </button>
                                    <button
                                        className={`nav-link ${changeTabUrl("labels") === path ? "active" : ""}`}
                                        id="tab-labels"
                                        type="button"
                                        role="tab"
                                        onClick={() => {
                                            changeTab("labels");
                                        }}
                                    >
                                        {t("main.litPalLabel")}
                                    </button>
                                    <button
                                        className={`nav-link ${changeTabUrl("material") === path ? "active" : ""}`}
                                        id="tab-material"
                                        type="button"
                                        role="tab"
                                        onClick={() => {
                                            changeTab("material");
                                        }}
                                    >
                                        {t("main.litWomMaterial")}
                                    </button>
                                    <button
                                        className={`nav-link ${changeTabUrl("operations") === path ? "active" : ""}`}
                                        id="tab-operations"
                                        type="button"
                                        role="tab"
                                        onClick={() => {
                                            changeTab("operations");
                                        }}
                                    >
                                        {t("main.litWoOpe")}
                                    </button>
                                    {showBom ? (
                                        <button
                                            className={`nav-link ${changeTabUrl("bom") === path ? "active" : ""}`}
                                            id="tab-bom"
                                            type="button"
                                            role="tab"
                                            onClick={() => {
                                                changeTab("bom");
                                            }}
                                        >
                                            {t("main.billOfMaterials")}
                                        </button>
                                    ) : (
                                        ""
                                    )}
                                    {workorderManagementRedux?.orderData?.workorderBatchType === "M" ? (
                                        <button
                                            className={`nav-link ${changeTabUrl("batch") === path ? "active" : ""}`}
                                            id="tab-batch"
                                            type="button"
                                            role="tab"
                                            onClick={() => {
                                                changeTab("batch");
                                            }}
                                        >
                                            {t("main.batch")}
                                        </button>
                                    ) : (
                                        ""
                                    )}
                                    {showBob ? (
                                        <button
                                            className={`nav-link ${changeTabUrl("bob") === path ? "active" : ""}`}
                                            id="tab-bob"
                                            type="button"
                                            role="tab"
                                            onClick={() => {
                                                changeTab("bob");
                                            }}
                                        >
                                            BoB
                                        </button>
                                    ) : (
                                        ""
                                    )}
                                    <button
                                        className={`nav-link ${changeTabUrl("test") === path ? "active" : ""}`}
                                        id="tab-test"
                                        type="button"
                                        role="tab"
                                        onClick={() => {
                                            changeTab("test");
                                        }}
                                    >
                                        Test
                                    </button>
                                    &nbsp;
                                </div>
                            </nav>
                        </div>
                    </div>

                    <div className="col-3">
                        <div className="float-right workorder-management-searchbox">
                            <div className="input-group mb-3">
                                <input
                                    type="text"
                                    id="txtWorkorder"
                                    className="form-control"
                                    placeholder={t("main.workOrder")!}
                                    aria-label="Date Start"
                                    aria-describedby="date-start"
                                    onChange={e => {dispatch(onSetWorkorderSearch(e.currentTarget.value))}}
                                />
                                <div className="input-group-append">
                                    <button
                                        id="btnSearchWorkorder"
                                        className="btn btn-primary"
                                        type="button"
                                        onClick={e => {
                                            dispatch(onSetShowClosed(false));
                                            dispatch(onSetSelectedWorkorders([]));
                                            dispatch(getWorkorderItemsRedux(0) as any);
                                            navigate('/workordermanagement');
                                        }}
                                    >
                                        <span
                                            id="btnRefreshSpinnerWrk"
                                            className="spinner-border spinner-border-sm d-none"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        {t("main.search")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-4">
                        <h5 id="workorderNumber" className="nav-link p-1 m-0 mr-0 align-content-center">
                            {workorderManagementRedux.orderData?.workorder ?? "..."}
                        </h5>
                    </div>
                    <div className="col-4"></div>
                    <div className="col-4"></div>
                </div>
            </div>

            <div className="tab-content mt-2" id="nav-tabContent">
                {props.children}
            </div>
        </React.Fragment>
    );
};

export default WorkorderManagementEdit;
